import { useParams, useNavigate } from 'react-router-dom';
import { Fragment, useState } from 'react';
import {
  useAnalysisCluster, useDetachClusterItem, useSynthesizeCluster, useUpdateCluster,
  useUpdateClusterShare,
} from '../hooks/useAnalysisClusters';
import Badge from '../components/ui/Badge';
import ScorePill from '../components/ui/ScorePill';
import Modal from '../components/ui/Modal';

const PRIORITY_MAP = {
  high: { bg: '#FEE2E2', color: '#EF4444', label: 'Hoch' },
  medium: { bg: '#FEF3C7', color: '#F59E0B', label: 'Mittel' },
  low: { bg: '#D1FAE5', color: '#10B981', label: 'Gering' },
};

function Paragraphs({ text }) {
  if (!text) return null;
  return text.split(/\n\n+/).map((p, i) => (
    <p key={i} className="text-[14px] text-gray-700 leading-relaxed mb-3 last:mb-0">{p}</p>
  ));
}

export default function AnalysisCluster() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: cluster, isLoading } = useAnalysisCluster(id);
  const detach = useDetachClusterItem();
  const synth = useSynthesizeCluster();
  const updateShare = useUpdateClusterShare();

  const [shareOpen, setShareOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sharePassword, setSharePassword] = useState('');
  const [copied, setCopied] = useState(false);

  if (isLoading || !cluster) return <div className="text-gray-400 mt-12 text-center">Wird geladen…</div>;

  const shareUrl = cluster.share_token
    ? `${window.location.origin}/share/cluster/${cluster.share_token}`
    : '';

  const r = cluster.result || {};
  const isReady = cluster.status === 'ready';
  const isSynth = cluster.status === 'synthesizing';
  const isStartingSynth = synth.isPending;
  const isBusy = isSynth || isStartingSynth;
  const synthErrorMsg = synth.isError
    ? (synth.error?.response?.data?.error === 'no_items'
        ? 'Der Cluster enthält keine Analysen.'
        : (synth.error?.response?.data?.message || synth.error?.message || 'Unbekannter Fehler.'))
    : null;

  return (
    <div>
      <div className="flex items-start justify-between mb-5 gap-4">
        <div>
          <button onClick={() => navigate('/cro/clusters')} className="text-[12px] text-gray-500 hover:text-gray-700 mb-1.5 block">← Zurück zu Clustern</button>
          <h1 className="text-[23px] font-bold mb-0.5">{cluster.title}</h1>
          <p className="text-gray-500 text-[13.5px]">
            {cluster.analyses?.length || 0} Analysen
            {cluster.discipline_mix ? ` · Disziplinen: ${cluster.discipline_mix}` : ''}
            {cluster.provider ? ` · ${cluster.provider}` : ''}
            {cluster.is_shared && <span className="ml-2 text-teal">· geteilt</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {isReady && (
            <>
              <button
                onClick={() => setPreviewOpen(true)}
                className="px-3 py-2.5 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50"
              >
                Vorschau
              </button>
              <button
                onClick={() => window.open(`/api/analysis-clusters/${cluster.id}/pdf`, '_blank')}
                className="px-3 py-2.5 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50"
              >
                PDF
              </button>
              <button
                onClick={() => window.open(`/api/analysis-clusters/${cluster.id}/pptx`, '_blank')}
                className="px-3 py-2.5 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50"
              >
                PPT
              </button>
              <button
                onClick={() => setShareOpen(true)}
                className="px-3 py-2.5 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50"
              >
                {cluster.is_shared ? 'Link verwalten' : 'Teilen'}
              </button>
            </>
          )}
          <button
            onClick={() => synth.mutate(cluster.id)}
            disabled={isBusy || cluster.analyses?.length === 0}
            className="px-4 py-2.5 bg-sidebar text-white rounded-lg text-[13px] font-medium hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {isBusy && (
              <span className="w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden />
            )}
            {isStartingSynth
              ? 'Wird gestartet…'
              : isSynth
                ? 'Synthese läuft…'
                : isReady ? '↻ Neu synthetisieren' : 'Synthese starten →'}
          </button>
        </div>
      </div>

      {isBusy && (
        <div className="bg-white rounded-xl border border-teal/30 shadow-sm p-5 mb-5">
          <div className="flex items-start gap-3 mb-3">
            <span className="w-5 h-5 mt-0.5 rounded-full border-2 border-teal/30 border-t-teal animate-spin flex-shrink-0" aria-hidden />
            <div className="flex-1">
              <div className="font-semibold text-[14px] text-sidebar">
                {isStartingSynth ? 'Synthese wird gestartet …' : 'Synthese läuft im Hintergrund'}
              </div>
              <div className="text-[12.5px] text-gray-500 mt-0.5 leading-relaxed">
                Das LLM verdichtet {cluster.analyses?.length || 0} Einzelanalysen zu einer
                projektweiten Roadmap. Dauer typischerweise 30-90 Sekunden. Die Seite
                aktualisiert sich automatisch, sobald die Synthese fertig ist.
              </div>
            </div>
          </div>
          <div className="progress-indeterminate-track">
            <div className="progress-indeterminate-bar" />
          </div>
        </div>
      )}

      {synth.isError && !isBusy && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5 text-red-800 text-[13px] flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold mb-0.5">Synthese konnte nicht gestartet werden</div>
            <div className="text-red-700">{synthErrorMsg}</div>
          </div>
          <button
            onClick={() => synth.reset()}
            className="text-red-700 hover:text-red-900 text-[11px] font-medium flex-shrink-0"
          >
            Schließen
          </button>
        </div>
      )}

      {(() => {
        const total = cluster.analyses?.length || 0;
        const done = cluster.analyses?.filter(a => a.status === 'done').length || 0;
        const pending = cluster.analyses?.filter(a => a.status === 'pending' || a.status === 'processing').length || 0;
        if (pending > 0) {
          return (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5 text-blue-800 text-[13px]">
              {done} von {total} Sub-Analysen abgeschlossen, {pending} noch in Arbeit. Die Synthese sollte erst gestartet werden, wenn alle fertig sind.
            </div>
          );
        }
        return null;
      })()}

      {/* Synthese-Output */}
      {isReady && r && (
        <div className={`space-y-4 mb-6 transition-opacity ${isBusy ? 'opacity-40 pointer-events-none select-none' : ''}`}>
          {r.narrative?.intro && (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
              <h3 className="font-bold text-[16px] mb-3">Synthese · Einleitung</h3>
              <Paragraphs text={r.narrative.intro} />
            </div>
          )}

          {r.discipline_overlap && (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
              <h3 className="font-bold text-[16px] mb-3">Disziplin-Übergreifende Effekte</h3>
              <Paragraphs text={r.discipline_overlap} />
            </div>
          )}

          {r.patterns?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
              <h3 className="font-bold text-[16px] mb-3">Erkannte Muster ({r.patterns.length})</h3>
              <div className="space-y-3">
                {r.patterns.map((p, i) => {
                  const pr = PRIORITY_MAP[p.priority] || PRIORITY_MAP.medium;
                  return (
                    <div key={i} className="border-l-4 pl-4 py-2" style={{ borderColor: pr.color }}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-[14px]">{p.pattern}</span>
                        <Badge bg={pr.bg} color={pr.color}>{pr.label}</Badge>
                        <span className="text-[11px] text-gray-400">{p.occurrences} Vorkommen</span>
                      </div>
                      <p className="text-[13px] text-gray-700 leading-relaxed">{p.description}</p>
                      {p.affected_urls?.length > 0 && (
                        <div className="text-[11px] text-gray-400 mt-1 truncate">
                          Betrifft: {p.affected_urls.join(', ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {r.contradictions?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <h3 className="font-semibold text-[14px] text-amber-900 mb-2">Widersprüche zwischen Analysen</h3>
              <ul className="space-y-2">
                {r.contradictions.map((c, i) => (
                  <li key={i} className="text-[13px] text-amber-900">
                    <span className="font-semibold">{c.title}:</span> {c.description}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {r.top_measures?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="font-bold text-[16px]">Projektweite Roadmap ({r.top_measures.length})</h3>
                <p className="text-[12px] text-gray-500 mt-0.5">Priorisiert über alle Analysen hinweg</p>
              </div>
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="px-6 py-3 text-gray-500 font-medium w-8">#</th>
                    <th className="px-6 py-3 text-gray-500 font-medium">Maßnahme</th>
                    <th className="px-6 py-3 text-gray-500 font-medium w-20">Disziplin</th>
                    <th className="px-6 py-3 text-gray-500 font-medium w-24">Priorität</th>
                    <th className="px-6 py-3 text-gray-500 font-medium w-24">Aufwand</th>
                    <th className="px-6 py-3 text-gray-500 font-medium w-24">Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {r.top_measures.map((m, i) => {
                    const pr = PRIORITY_MAP[m.priority] || PRIORITY_MAP.medium;
                    const eMap = { low: 'Niedrig', medium: 'Mittel', high: 'Hoch' };
                    return (
                      <Fragment key={i}>
                        <tr className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50">
                          <td className="px-6 py-3.5 font-bold text-gray-400 align-top">{m.nr}</td>
                          <td className="px-6 py-3.5 align-top">
                            <div className="font-medium mb-1">{m.measure}</div>
                            {m.rationale && (
                              <div className="text-[12px] text-gray-500 leading-relaxed">{m.rationale}</div>
                            )}
                            {m.source_url && (
                              <div className="text-[11px] text-gray-400 font-mono mt-1 truncate">aus: {m.source_url}</div>
                            )}
                          </td>
                          <td className="px-6 py-3.5 text-[11px] text-gray-500 align-top">{m.discipline || '—'}</td>
                          <td className="px-6 py-3.5 align-top"><Badge bg={pr.bg} color={pr.color}>{pr.label}</Badge></td>
                          <td className="px-6 py-3.5 text-[12px] align-top">{eMap[m.effort] || m.effort}</td>
                          <td className="px-6 py-3.5 font-medium text-teal align-top">{m.conv_impact}</td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {r.narrative?.conclusion && (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
              <h3 className="font-bold text-[16px] mb-3">Fazit & Roadmap-Empfehlung</h3>
              <Paragraphs text={r.narrative.conclusion} />
            </div>
          )}
        </div>
      )}

      {/* Cluster-Items */}
      <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-bold text-[16px]">Bestandteile ({cluster.analyses?.length || 0})</h3>
        </div>
        {cluster.analyses?.length > 0 ? (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="px-6 py-3 text-gray-500 font-medium">URL</th>
                <th className="px-6 py-3 text-gray-500 font-medium w-20">Disziplin</th>
                <th className="px-6 py-3 text-gray-500 font-medium w-24">Typ</th>
                <th className="px-6 py-3 text-gray-500 font-medium w-20">Score</th>
                <th className="px-6 py-3 text-gray-500 font-medium w-20 text-right">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {cluster.analyses.map((a) => (
                <tr key={a.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50">
                  <td className="px-6 py-3 truncate max-w-[400px]">
                    <button onClick={() => navigate(`/cro/analyses/${a.id}`)} className="text-teal hover:underline">{a.url}</button>
                  </td>
                  <td className="px-6 py-3 text-[11px] text-gray-500">{(a.discipline || 'cro').toUpperCase()}</td>
                  <td className="px-6 py-3"><Badge bg="#F3F4F6" color="#6B7280">{a.page_type}</Badge></td>
                  <td className="px-6 py-3">{a.score ? <ScorePill score={a.score} /> : '—'}</td>
                  <td className="px-6 py-3 text-right">
                    <button
                      onClick={() => detach.mutate({ id: cluster.id, itemId: a.pivot?.id })}
                      className="text-[11px] text-red-500 hover:text-red-700"
                    >
                      Entfernen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="py-8 text-center text-gray-400 text-sm">Keine Analysen im Cluster.</div>
        )}
      </div>

      {previewOpen && (
        <Modal onClose={() => setPreviewOpen(false)}>
          <div className="w-[min(1100px,95vw)] h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[17px] font-bold">{cluster.title}</div>
                <div className="text-[12px] text-gray-500">PDF-Vorschau</div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => window.open(`/api/analysis-clusters/${cluster.id}/pdf`, '_blank')}
                  className="px-3 py-1.5 bg-teal text-white rounded-lg text-[12px] font-medium"
                >
                  PDF öffnen
                </button>
                <button
                  onClick={() => setPreviewOpen(false)}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-[12px]"
                >
                  Schließen
                </button>
              </div>
            </div>
            <iframe
              title="cluster-preview"
              src={`/api/analysis-clusters/${cluster.id}/html`}
              className="flex-1 w-full border border-gray-200 rounded-lg bg-white"
            />
          </div>
        </Modal>
      )}

      {shareOpen && (
        <Modal onClose={() => setShareOpen(false)}>
          <div className="w-[460px]">
            <h2 className="text-[17px] font-bold mb-1">Share-Link</h2>
            <p className="text-[13px] text-gray-500 mb-4">
              Cluster-Synthese teilen — öffentlicher Link, optional mit Passwort.
            </p>

            {!cluster.is_shared && (
              <div className="bg-bg rounded-lg p-3 mb-4 text-[12.5px] text-gray-600">
                Sharing ist noch nicht aktiviert. Erst aktivieren, dann Link kopieren oder Passwort setzen.
                <button
                  onClick={async () => {
                    await updateShare.mutateAsync({ id: cluster.id, is_shared: true });
                  }}
                  className="mt-3 w-full px-3 py-2 bg-teal text-white rounded-lg text-[12px] font-medium"
                >
                  Sharing aktivieren
                </button>
              </div>
            )}

            {cluster.is_shared && (
              <>
                <div className="bg-bg rounded-lg p-3 mb-4">
                  <div className="text-[11px] text-gray-500 mb-1">Share-URL</div>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={shareUrl}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-[12px] font-mono bg-white outline-none"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(shareUrl);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="px-3 py-2 bg-teal text-white rounded-lg text-[12px] font-medium"
                    >
                      {copied ? 'Kopiert!' : 'Kopieren'}
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">
                    Passwort setzen (optional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Mindestens 4 Zeichen"
                      value={sharePassword}
                      onChange={(e) => setSharePassword(e.target.value)}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
                    />
                    <button
                      onClick={async () => {
                        if (sharePassword.length < 4) return;
                        await updateShare.mutateAsync({
                          id: cluster.id,
                          is_shared: true,
                          share_password: sharePassword,
                        });
                        setSharePassword('');
                      }}
                      disabled={sharePassword.length < 4}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-[12px] hover:bg-gray-50 disabled:opacity-50"
                    >
                      Setzen
                    </button>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={async () => {
                      await updateShare.mutateAsync({ id: cluster.id, is_shared: false });
                      setShareOpen(false);
                    }}
                    className="px-3 py-2 text-red-600 text-[12px] hover:underline"
                  >
                    Sharing deaktivieren
                  </button>
                  <button
                    onClick={() => setShareOpen(false)}
                    className="px-4 py-2 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50"
                  >
                    Schließen
                  </button>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
